<template>
  <SectionTemplate title="常见问题（FAQs）">
    <div class="faq-container">
      <div v-for="(item, index) in faqList" :key="index" class="faq-item">
        <div class="faq-question" :class="{ open: item.isOpen }" @click="toggleFaq(index)">
          <Text class="faq-question-title" as="h3" :variant="titleFontSize">{{
            item.question
          }}</Text>
          <IconMore class="icon-wrapper" :class="{ rotate: item.isOpen }" />
        </div>
        <div class="faq-answer" :class="{ open: item.isOpen }">
          <div class="answer-content">
            <p v-html="item.answer" />
          </div>
        </div>
      </div>
    </div>
  </SectionTemplate>
</template>

<script setup lang="ts">
import SectionTemplate from '@/design/widgets/landing-section/cn-template.vue';
import { PositionCode, useContactUs } from '@rendora/common/features/contact';
import { Text } from '@rendora/design/components/text';
import { useBreakpoint } from '@rendora/design/index';
import IconMore from '~/assets/svg/icon-dropdown.svg?component';
const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);

const titleFontSize = computed(() => (isMobile.value ? '16' : '24'));

interface FaqItem {
  question: string;
  answer: string;
  isOpen: boolean;
}

const links = {
  '3d-ai-avatar': 'https://www.youyan3d.com/blog/What-is-3D-avatar.html',
  '3d-avatar-vs-2d-avatar':
    'https://www.youyan3d.com/blog/Differences-Between-3D-and-2D-avatars.html',
  '3d-avatar-library': 'https://www.youyan3d.com/characters',
  'try-now': 'https://www.youyan3d.com/login',
  'natural-expressive':
    'https://www.youyan3d.com/blog/Text-to-3D-Animation-Generate-AI-Digital-Human-Animation-by-Input-Script.html',
  'multi-language-tts':
    'https://www.youyan3d.com/blog/Master-Multilingual-Videos-from-Translation-to-Voice.html',
  'youling-platform': 'https://www.youling3d.com/',
};

type LinkKey = keyof typeof links;

const getLink = (key: LinkKey, title: string) => {
  return links[key] ? `<a href="${links[key] as string}" target="_blank">${title}</a>` : title;
};

const getStrong = (text: string) => {
  return `<strong>${text}</strong>`;
};

const faqList = ref<FaqItem[]>([
  {
    question: '什么是3D AI数字人？可以用来做什么？',
    answer: `${getLink('3d-ai-avatar', '3D AI数字人')}是通过三维图形学与AI技术驱动的虚拟人物，不仅拥有逼真的形象，还具备自然的语音、流畅的动作和生动的情绪表达能力。你可以将它理解为一个既“有形象”，又“能表达”，还“够专业”的 AI 数字员工，能够替代真人完成视频讲解、活动主持、智能交互、直播带货等各类任务。
在魔珐有言-3D数字人AI视频生成平台，数字人可以替代真人出镜——用户只需选择一个数字人形象、输入脚本，就能生成专业级的视频内容。无论是做产品宣传、员工培训、活动主持，还是知识讲解，它都能高效完成出镜任务，帮助企业和个人实现低门槛、高质量、高效率、规模化的视频创作。`,
    isOpen: true,
  },
  {
    question: '魔珐有言的数字人和其他平台有什么不同？',
    answer: `市面上许多平台的数字人，实际上只是用真人视频素材做简单的唇形替换，只能呈现单一视角、人物动作僵硬、表情重复，服装与场景也不可替换，无法满足专业内容创作的灵活性和质量要求。
魔珐有言基于自研的3D数字人全栈式AI技术，在人物质量、人物表现力、视频质量和可编辑性等方面实现全面升级：
1. 人物质量更高
有言提供超3000款好莱坞级超写实3D数字人，覆盖不同性别、年龄、族裔与风格，还支持个性化形象编辑，轻松打造出千人千面的专属角色。
2. 表达更自然生动（语音 + 表情 + 动作）
借助AI语音合成与动作生成技术，有言3D AI数字人不仅“能说话”，还能根据语义生成高度契合的表情与肢体动作，实现如同真人一般的生动表现力。
3. 视频效果更专业
不同于2D数字人常见的“纸片人”贴图效果，有言内置近千款高质量3D场景，并支持AI多机位运镜与专业灯光渲染，让每一条视频具备发布会级效果。
4. 更精准灵活的视频编辑能力
有言支持对脚本、素材，数字人形象、声音、动作、表情，场景、镜头、包装等全要素进行精细编辑和控制。用户可以像导演一样精准控制视频中的每一个细节。
了解更多 →${getLink('3d-avatar-vs-2d-avatar', '《3D数字人和2D数字人有什么区别？》')}`,
    isOpen: false,
  },
  {
    question: '魔珐有言提供多少种数字人形象？还会继续扩充？',
    answer: `目前魔珐有言已上线超${getLink('3d-avatar-library', '3000款高质量3D数字人')}，涵盖不同年龄、肤色、风格，覆盖从销售顾问、培训讲师、企业高管到政务导办员、医疗护士、新闻主播、教育教师等多种职业角色，适配不同用户的业务需求和内容场景。
我们还在持续扩充角色库，每月新增超百款3D数字人形象，让用户拥有更加丰富多样的出镜选择。
你可以访问${getLink('3d-avatar-library', '「3D数字人角色库」')}浏览全部角色，并通过年龄、性别、风格、语言等维度快速筛选，找到最符合你需求的“数字人主角”。`,
    isOpen: false,
  },
  {
    question: '数字人服装、发型、妆容等形象元素能否在线编辑与替换？',
    answer: `可以。有言支持3D数字人形象的在线编辑，包括服装、发型、妆容、饰品、五官脸型等多种外观元素，轻松打造贴合内容语境和品牌调性的专属数字人角色。
所有形象编辑都可在平台内实时预览，无需绿幕抠图、也无需3D建模基础，即可快速完成角色设定。你还可以将编辑后的形象保存为“我的人物”，在不同视频项目中一键调用，提升创作效率与统一性。
立即${getLink('try-now', '注册体验')}，自定义你的专属3D数字人形象，开启高质量AI视频创作之旅。`,
    isOpen: false,
  },
  {
    question: '是否支持企业专属数字人定制或照片克隆3D形象？',
    answer: `支持。有言提供专业的3D数字人定制服务，帮助企业打造更具辨识度、更贴合自身品牌形象的数字人角色。
1. 3D数字人克隆：支持上传真人照片与音频素材，生成专属3D AI数字人形象，1:1还原人物外貌与声线，广泛应用于品牌代言人打造、创始人/高管对外宣传等场景。
2. 原创形象定制：可根据内容场景与视觉风格，定制一位全新形象的数字人角色，无论是科技感主播、专业讲解员，还是带有地域文化或行业特征的虚拟IP，都可灵活设计，让每一次出镜都代表企业独特表达。
定制完成的数字人可直接在有言平台调用，进行各类AI视频创作，也可与有灵-3D数字人智能体平台打通，实现智能客服、AI助教、大屏讲解、业务导办等交互场景。
想了解定制方式与示例？欢迎<a href="javascript:void(0)" data-action="contact">联系企业顾问</a>，获取专属定制方案。`,
    isOpen: false,
  },
  {
    question: '动作与表情是否可根据语义自动生成，并支持风格化控制？',
    answer: `魔珐有言的3D AI数字人能够根据输入的文字脚本，自动生成与语义高度匹配的动作和表情，无论是点头、指向、挥手等多种手势，还是开心、困惑、严肃等情绪，都能通过AI智能生成，让数字人拥有如同真人一般${getLink('natural-expressive', '自然生动的表现力')}。
此外，还提供风格化动作控制能力，用户可以根据视频场景、受众偏好，自由选择数字人的动作风格——比如在企业培训、课程讲解时选择自然沉稳的动作风格，在营销带货或社媒短视频场景中切换更活泼有感染力的风格，确保表达始终贴合需求。
想体验数字人自动生成动作表情的效果？${getLink('try-now', '立即注册体验')}`,
    isOpen: false,
  },
  {
    question: '魔珐有言的语音TTS提供多少语种 / 音色，接近真人吗？',
    answer: `魔珐有言支持${getLink('multi-language-tts', '30+主流语言')}、${getLink('multi-language-tts', '400+音色')}，覆盖英文、法语、德语、西班牙语、阿拉伯语、日语、韩语等主流语种，并提供包括美式/英式英语、闽南语、粤语在内的多种口音，轻松适配跨境传播与本地化视频制作需求。
每种语言下都提供多种风格音色选择，从专业主持、亲切讲解到直播带货，语音自然流畅、语调富有情感，可精准匹配不同类型内容的表达需求，让数字人拥有如同专业配音演员般的声音表现力。`,
    isOpen: false,
  },
  {
    question: '魔珐有言的AI数字人可以实时交互吗？',
    answer: `支持。如果您希望实现数字人实时交互能力，可通过魔珐旗下的另一款产品${getLink('youling-platform', '【魔珐有灵-3D数字人智能体平台】')}实现。
魔珐有灵是一款面向企业与个人的AI数字人智能体平台，支持零代码快速创建具备实时交互能力的3D数字人。数字人不仅具备专业形象和如真人般自然生动的表达能力，还可接入主流大模型与企业知识库，精准理解业务语境、即时作出准确回应，帮助企业构建7×24小时在线、低成本、可规模化复制的智能服务能力。
魔珐有灵已广泛应用于智能客服、AI助教、大屏讲解、业务导办等交互场景，并可灵活部署于PC、Web、移动、线下展厅大屏或BI超大屏等多种终端形态，满足不同业务触点下的应用需求。
想了解如何为你的业务接入交互式数字人？欢迎<a href="javascript:void(0)" data-action="contact">联系我们</a>，获取专属1V1咨询与产品演示服务。`,
    isOpen: false,
  },
]);

const toggleFaq = (index: number) => {
  faqList.value[index].isOpen = !faqList.value[index].isOpen;
};

const contactUs = () => {
  useContactUs(1078922 as PositionCode, '企业级数字人定制', '1V1专属咨询服务');
};

// 监听页面点击事件，处理 v-html 中的链接点击
onMounted(() => {
  document.addEventListener('click', handleClick);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClick);
});

const handleClick = (event: Event) => {
  const target = event.target as HTMLElement;
  if (target.tagName === 'A' && target.dataset.action === 'contact') {
    event.preventDefault();
    contactUs();
  }
};
</script>

<style lang="scss" scoped src="./styles/mobile.scss" />
<style lang="scss" scoped src="./styles/pc.scss" />
